/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: CreateProjectRoleDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * CreateProjectRoleDialog - Frontend UI module: CreateProjectRoleDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блок отправки.
 * END_CHANGE_SUMMARY
 */

import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isIntegerInRange, isNonEmptyTrimmed } from '../../../utils/formValidators';
import RoleWorkloadBar, { clampWorkloadPercent } from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';
import ConcurrentRolesSelect from './ConcurrentRolesSelect';

/* START_METHOD_CreateProjectRoleDialog */
/* START_CONTRACT: CreateProjectRoleDialog
 * PURPOSE: Диалог создания проектной роли (название, сокр., порядок, загрузка, совместные роли).
 * INPUTS: { open: boolean, onClose: ()=>void, onSubmit: (payload)=>Promise, existingRoles: ProjectRole[] }.
 * OUTPUTS: { JSX.Element - диалог }.
 * SIDE_EFFECTS: вызов onSubmit (HTTP наверх); локальный state.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: CreateProjectRoleDialog
 */
export default function CreateProjectRoleDialog({ open, onClose, onSubmit, existingRoles }) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const shortNameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const workloadField = useBlurValidatedField('100', isIntegerInRange(0, 100), formReady);
  const [sortOrder, setSortOrder] = useState('');
  const [concurrentRoles, setConcurrentRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const canSave = nameField.isValid && shortNameField.isValid && workloadField.isValid;
  const previewWorkload = workloadField.isValid
    ? clampWorkloadPercent(workloadField.value)
    : 0;
  const previewAccent = nameField.value.trim()
    ? getColorByName(nameField.value.trim())
    : undefined;

  /* START_METHOD_handleSubmit */
  /* START_CONTRACT: handleSubmit
   * PURPOSE: Собрать payload роли и создать её; при успехе закрыть диалог.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onSubmit (HTTP); setLoading; handleClose.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleSubmit
   */
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      nameField.touch();
      shortNameField.touch();
      workloadField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    try {
      const workloadValue = Number(workloadField.value);
      const payload = {
        name: nameField.value.trim(),
        shortName: shortNameField.value.trim(),
        workloadPercent: workloadValue,
        concurrentRoleIds: concurrentRoles.map((role) => role.id),
      };
      if (sortOrder !== '') {
        payload.sortOrder = Number(sortOrder);
      }
      await onSubmit(payload);
      /* START_BLOCK_SUBMIT_OK */
      handleClose();
      /* END_BLOCK_SUBMIT_OK */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSubmit */

  /* START_METHOD_handleClose */
  /* START_CONTRACT: handleClose
   * PURPOSE: Сбросить поля формы и закрыть диалог.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setState reset; onClose.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleClose
   */
  const handleClose = () => {
    if (loading) return;
    nameField.reset('');
    shortNameField.reset('');
    workloadField.reset('100');
    setSortOrder('');
    setConcurrentRoles([]);
    resetFormReady();
    onClose();
  };
  /* END_METHOD_handleClose */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: dialogTransitionSlotProps }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle sx={{ fontSize: '1rem' }}>Новая проектная роль</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Название"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={nameField.showError}
            helperText={nameField.showError ? 'Введите название роли' : ' '}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            required
            fullWidth
            label="Сокращённое название"
            value={shortNameField.value}
            onChange={shortNameField.onChange}
            onBlur={shortNameField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={shortNameField.showError}
            helperText={shortNameField.showError ? 'Введите сокращённое название' : ' '}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
        <TextField
          fullWidth
          label="Порядок сортировки"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value.replace(/[^\d-]/g, ''))}
          margin="normal"
          size="small"
          helperText="Оставьте пустым для автоматического назначения"
          type="text"
          inputMode="numeric"
        />
          <TextField
            fullWidth
            label="Процент загрузки"
            value={workloadField.value}
            onChange={(e) => workloadField.setValue(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={workloadField.onBlur}
            margin="normal"
            size="small"
            required
            disabled={loading}
            type="text"
            inputMode="numeric"
            error={workloadField.showError}
            helperText={workloadField.showError ? 'Целое число от 0 до 100' : ' '}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, mb: 1 }}>
            <RoleWorkloadBar
              value={previewWorkload}
              accentColor={previewAccent}
              size="lg"
            />
            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Так загрузка будет отображаться в списке ролей
            </Box>
          </Box>
        <ConcurrentRolesSelect
          label="Одновременная работа с ролями"
          roles={existingRoles}
          value={concurrentRoles}
          onChange={setConcurrentRoles}
          disabled={loading}
        />
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Создать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
/* END_METHOD_CreateProjectRoleDialog */
